import { Injectable } from '@angular/core';
import { Util } from '@libs/utilities/util';
import { Observable, Subscriber } from 'rxjs';

import { ISecureSseRequestOptions } from './sse-request.interface';


@Injectable()
export class SecureSseRequest {

	getServerSentEvent<T>(url: string, options: ISecureSseRequestOptions): Observable<T> {
		return new Observable<T>((observer: Subscriber<T>) => {
			const abortController: AbortController = new AbortController();
			let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

			fetch(url, {
				method	: options.method,
				headers	: {
					'Accept'		: 'text/event-stream',
					'Content-Type'	: 'application/json',
					...options.headers
				},
				...options.body
					? { body: Util.Basic.stringifyIfObject(options.body) }
					: {},
				signal: abortController.signal
			}).then(async (response: Response) => {
				if (!response.ok) throw await this.createHttpError(response);
				if (!response.body) throw new Error('SSE response did not contain a readable stream.');

				reader = response.body.getReader();
				await this.processStream(reader, observer);
			}).catch((error: unknown) => {
				if (abortController.signal.aborted) return;
				abortController.abort();
				observer.error(error);
			});

			return () => {
				reader?.cancel().catch(() => {});
				abortController.abort();
			};
		});
	}


	/**------------------------------------------------------
	 * Helper Functions
	 */
	private async createHttpError(response: Response): Promise<Error> {
		const fallbackMessage: string = `SSE request failed with status ${response.status}.`;
		try {
			const responseText: string = (await response.text()).trim();
			if (!responseText) return new Error(fallbackMessage);

			const responseData: ISecureSseErrorResponse | null = Util.Basic.parseIfValidJson(responseText);
			const serverMessage: string = responseData?.message
				|| responseData?.error?.message
				|| (responseText.length <= 300 ? responseText : '');
			return new Error(serverMessage || fallbackMessage);
		} catch {
			return new Error(fallbackMessage);
		}
	}

	private async processStream<T>(reader: ReadableStreamDefaultReader<Uint8Array>, observer: Subscriber<T>): Promise<void> {
		const decoder: TextDecoder = new TextDecoder('utf-8');
		let buffer: string = '';

		while (!observer.closed) {
			const response: ReadableStreamReadResult<Uint8Array> = await reader.read();
			buffer += decoder.decode(response.value, { stream: !response.done });
			buffer = this.normalizeLineEndings(buffer, response.done);

			const eventBlocks: string[] = buffer.split('\n\n');
			buffer = eventBlocks.pop() || '';

			for (const eventBlock of eventBlocks) {
				if (this.processEventBlock(eventBlock, observer)) return;
			}

			if (response.done) {
				if (buffer && this.processEventBlock(buffer, observer)) return;
				if (!observer.closed) observer.error(new Error('SSE stream ended without an explicit end event.'));
				return;
			}
		}
	}

	private normalizeLineEndings(value: string, isComplete: boolean): string {
		const hasPartialCarriageReturn: boolean = !isComplete && value.endsWith('\r');
		const normalizedValue: string = hasPartialCarriageReturn ? value.slice(0, -1) : value;
		return normalizedValue.replace(/\r\n|\r/g, '\n') + (hasPartialCarriageReturn ? '\r' : '');
	}

	private processEventBlock<T>(eventBlock: string, observer: Subscriber<T>): boolean {
		const lines: string[] = eventBlock.split('\n').filter((line: string) => !line.startsWith(':'));
		const eventType: string = lines.find((line: string) => line.startsWith('event:'))?.slice('event:'.length).trim() || 'message';
		const data: string = lines
			.filter((line: string) => line.startsWith('data:'))
			.map((line: string) => line.slice('data:'.length).trimStart())
			.join('\n');

		if (eventType === 'end') {
			observer.complete();
			return true;
		}
		if (!data) return false;
		const parsedData: T | null = Util.Basic.parseIfValidJson(data);
		if (parsedData === null) {
			observer.error(new Error('SSE response contained invalid JSON.'));
			return true;
		}

		if (eventType === 'error') {
			observer.error(parsedData);
			return true;
		}
		if (eventType === 'message' || eventType === 'failure') observer.next(parsedData);

		return false;
	}
}

interface ISecureSseErrorResponse {
	message?: string;
	error?	: { message?: string };
}
